/**
 * One read of the builder's world, shared by every screen under `/site`.
 *
 * `GET /api/v1/site/vocabulary` is the read the builder opens with — the nine
 * block schemas, the six templates, the four fonts, the two layout lists and
 * the profession's policy pack, in one call. It is **the service's description
 * of itself**, and every control on every screen below is drawn from it rather
 * than from a copy in this bundle. That is not tidiness: the validator and the
 * form would disagree the day one of them changed, and the firm would
 * experience it as "it saved and then it did not".
 *
 * Wave 4D found what happens when that rule is not kept. The template table,
 * the font list and the two layout vocabularies existed twice — once in the
 * service and once under `apps/web/site/` — and disagreed on four things at
 * once, invisibly (`tests/site-vocabulary.test.ts`).
 */

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { ApiError } from '../../lib/api'
import { site, type PreviewLink, type Site, type SiteVersion, type Vocabulary } from '../../lib/api/site'

interface SiteState {
  vocabulary: Vocabulary | null
  site: Site | null
  draft: SiteVersion | null
  published: SiteVersion | null
  isLive: boolean
  /** The firm's one standing preview link (C6, ADR-0024). It is on every read
   *  of this endpoint, because that read is where it is made and renewed. */
  preview: PreviewLink | null
  loading: boolean
  error: string | null
  /** Re-read the site and its versions. The vocabulary is shipped data and
   *  does not change under a running session, so it is read once. */
  refresh: () => Promise<void>
  setSite: (next: Site) => void
}

const Ctx = createContext<SiteState | null>(null)

export function SiteProvider({ children }: { children: ReactNode }) {
  const [vocabulary, setVocabulary] = useState<Vocabulary | null>(null)
  const [current, setCurrent] = useState<Site | null>(null)
  const [draft, setDraft] = useState<SiteVersion | null>(null)
  const [published, setPublished] = useState<SiteVersion | null>(null)
  const [isLive, setIsLive] = useState(false)
  const [preview, setPreview] = useState<PreviewLink | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    const body = await site.read()
    setCurrent(body.data.site)
    setDraft(body.data.draft)
    setPublished(body.data.published)
    setIsLive(body.data.is_live)
    setPreview(body.data.preview ?? null)
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    Promise.all([site.vocabulary(), site.read()])
      .then(([vocab, read]) => {
        if (cancelled) return
        setVocabulary(vocab.data)
        setCurrent(read.data.site)
        setDraft(read.data.draft)
        setPublished(read.data.published)
        setIsLive(read.data.is_live)
        setPreview(read.data.preview ?? null)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // Named as ours when it is ours, for the reason `lib/access.ts` gives
        // at length: a wrong diagnosis pointing at somebody else's component
        // sends a person to look in the wrong place.
        setError(err instanceof ApiError ? err.message : 'Your website could not be loaded.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <Ctx.Provider
      value={{
        vocabulary,
        site: current,
        draft,
        published,
        isLive,
        preview,
        loading,
        error,
        refresh,
        setSite: setCurrent,
      }}
    >
      {children}
    </Ctx.Provider>
  )
}

/** `useSite()` for a component that may be mounted outside `/site` (the edit
 *  bar's draft link) — `null` there rather than a throw. */
export function useOptionalSite(): SiteState | null {
  return useContext(Ctx)
}

export function useSite(): SiteState {
  const value = useContext(Ctx)
  if (!value) throw new Error('useSite outside SiteProvider')
  return value
}
